import { message } from 'antd';
import { CloseSquare, TickSquare } from 'iconsax-react';
import { useTranslation } from 'react-i18next';

const AUTH0_ERROR_CODE = {
  invalid_password: 'errors.auth.invalidPassword',
  invalid_grant: 'errors.auth.invalidCredentials',
  invalid_request: 'errors.auth.invalidRequest',
  access_denied: 'errors.auth.accessDenied',
  unauthorized: 'errors.auth.unauthorized',
  network_error: 'errors.auth.networkError',
  server_error: 'errors.auth.serverError',
  invalid_token: 'errors.auth.invalidToken',
  password_leaked: 'errors.auth.passwordLeaked',
  too_many_attempts: 'errors.auth.tooManyAttempts',
  unsupported_grant_type: 'errors.auth.unsupportedGrant',
  invalid_signup: 'errors.auth.invalidSignup',
};

interface AuthErrorData {
  code?: string;
  description?: string;
  error_description?: string;
  message?: string;
}

interface AuthError {
  data?: AuthErrorData;
  message?: string;
}

interface AuthResponse {
  access_token?: string;
  status?: number;
  data?: unknown;
}
export function getErrorMessage(error: AuthError | AuthResponse): string {
  let errMsg = 'errors.general';

  if (error?.data && typeof error.data === 'object') {
    const {
      code,
      description,
      error_description,
      message: serverMessage,
    } = error.data as AuthErrorData;

    if (code && AUTH0_ERROR_CODE[code]) {
      errMsg = AUTH0_ERROR_CODE[code];
    } else if (error_description) {
      errMsg = error_description;
    } else if (description) {
      errMsg = description;
    } else if (serverMessage) {
      errMsg = serverMessage;
    }
  } else if ('message' in error && error.message) {
    errMsg = error.message;
  }

  return errMsg;
}

export function useCheckAuthResponse() {
  const { t } = useTranslation();

  const showMessage = (
    type: 'success' | 'error',
    content: string,
    isSuccess: boolean,
    data?: unknown
  ) => {
    message[type]({
      content,
      className: `response-${type}`,
      type,
      icon:
        type === 'success' ? (
          <TickSquare
            size='24'
            variant='Bold'
            className='text-success !rounded-md mr-2'
          />
        ) : (
          <CloseSquare size='24' variant='Bold' className='text-error mr-2' />
        ),
    });

    return { success: isSuccess, data: isSuccess ? data : null };
  };

  const checkAuthResponse = (
    res: AuthResponse,
    type?: 'login' | 'signUp' | 'forgotPassword',
    customMessage?: string
  ) => {
    const successMessage =
      customMessage || (type ? t(`notification.success.${type}`) : null);

    if (res?.access_token) {
      return showMessage(
        'success',
        successMessage || t('notification.success.login'),
        true,
        res.data
      );
    }

    if (type === 'forgotPassword' && res?.status !== 400) {
      return showMessage(
        'success',
        successMessage || t('notification.success.forgotPassword'),
        true
      );
    }

    if (
      type === 'signUp' &&
      res?.status &&
      res?.status >= 200 &&
      res?.status < 300
    ) {
      return showMessage(
        'success',
        successMessage || t('notification.success.signUp'),
        true
      );
    }

    if (res?.status && res.status >= 400) {
      return showMessage(
        'error',
        customMessage || t(getErrorMessage(res)),
        false
      );
    }

    return { success: false, data: null };
  };
  return {
    checkAuthResponse,
    t,
  };
}
